import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { ButtonsWrapper, CommentWrapper, RepliesToggle } from './style';
import { CTA, CircleImage, HtmlRaw, Icon, Text } from '../../../../atoms';
import formatTimestamp from '../../../../../utils/formatTimestamp';
import CustomTextarea from '../../../../atoms/CustomTextarea';
import {
  DOCUMENT_EXPERT_GET_COMMENTS,
  DOCUMENT_EXPERT_SET_COMMENT,
  DOCUMENT_EXPERT_SET_COMMENT_REPLY,
} from '../../../../../app/redux/actions/documentsExpert';
import { withMediaQueries } from '../../../../../app/hoc/withMediaQueries';

const RenderComment = ({ comment, allow_reply, sendReply, isResponse }) => {
  const [showReplies, setshowReplies] = useState(false);
  const [showResponseArea, setShowResponseArea] = useState(false);
  const [response, setResponse] = useState('');

  return (
    <div>
      <div className="personal-info">
        <CircleImage
          size={isResponse ? 40 : 56}
          src={comment?.profile_pic}
          name={comment}
        />
        <div>
          <Text type="h3">
            {comment?.first_name} {comment?.last_name}
            {/* //? no need for badge as shown in figma, all users are experts (or supervisor) */}
            {comment?.created && (
              <span className="date">
                {formatTimestamp(comment.created * 1000)}
              </span>
            )}
          </Text>
          <Text type="b4">
            {comment?.workDivision?.value} - {comment?.workRole?.value}
          </Text>
        </div>
        {comment?.replies?.length > 0 && (
          <div className="replies-number">
            {comment?.replies?.length}
            <Icon type="message-circle-02" size={18} clickable={false} />
          </div>
        )}
      </div>
      <Text type="b4" mb={32} className="comment-body">
        <HtmlRaw html={comment?.body?.replaceAll(/\r\n|\r|\n/g, '<br />')} />
      </Text>
      {allow_reply && !isResponse && (
        <>
          {showResponseArea && (
            <CustomTextarea
              value={response}
              onChange={(value) => setResponse(value)}
              id="doc-comments-textarea"
            />
          )}
          <ButtonsWrapper>
            {showResponseArea ? (
              <>
                <CTA
                  type="secondary"
                  btnText="Annulla"
                  onClick={() => setShowResponseArea(!showResponseArea)}
                />
                <CTA
                  btnText="Conferma"
                  onClick={() => {
                    sendReply({
                      cid: comment?.id,
                      body: {
                        comment_body: response,
                      },
                    });
                    setShowResponseArea(false);
                    setResponse('');
                  }}
                  disabled={!response}
                />
              </>
            ) : (
              <CTA
                btnText="Rispondi"
                bottomRight
                onClick={() => setShowResponseArea(!showResponseArea)}
              />
            )}
          </ButtonsWrapper>
        </>
      )}
      {comment?.replies?.length > 0 && (
        <div>
          <RepliesToggle onClick={() => setshowReplies(!showReplies)}>
            <Icon type="chevron-down" rotate={showReplies ? 180 : 0} />
            <Text type="b3">{comment?.replies?.length} risposte</Text>
          </RepliesToggle>
          {showReplies && (
            <div className="replies">
              {(comment?.replies || []).map((c, i) => (
                <div key={`reply-${i}`}>
                  <RenderComment
                    comment={c}
                    allow_reply={allow_reply}
                    sendReply={sendReply}
                    isResponse
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
RenderComment.propTypes = {
  comment: PropTypes.object,
  allow_reply: PropTypes.bool,
  sendReply: PropTypes.func,
  isResponse: PropTypes.bool,
};

const DocumentExpertComments = ({
  getComments,
  comments,
  allow_reply,
  username,
  sendComment,
  sendReply,
  mediaIsPhone,
}) => {
  const [myComment, setMyComment] = useState('');
  const { document_id: id } = useParams();

  useEffect(() => {
    if (id) getComments(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleOnSubmit = () => {
    sendComment({
      id,
      body: {
        comment_body: myComment,
      },
    });
    setMyComment('');
  };

  const handleSendReply = ({ cid, body }) => sendReply({ id, cid, body });

  return (
    <div>
      <Text type="h1.5" mb={24}>
        Feedback
      </Text>
      <CommentWrapper>
        <div className="personal-info">
          <CircleImage size={56} src={username?.profile_pic} name={username} />
          <div>
            <Text type="h3">
              {username?.first_name} {username?.last_name}
            </Text>
            <Text type="b4">
              {username?.work_division?.value} - {username?.work_role?.value}
            </Text>
          </div>
        </div>
        <textarea
          placeholder="Inserisci qui il tuo testo"
          onChange={(e) => setMyComment(e.target.value)}
          value={myComment}
        />
        <CTA
          btnText="Pubblica"
          onClick={handleOnSubmit}
          disabled={myComment.length <= 0}
          bottomRight
          fullsize={mediaIsPhone}
        />
      </CommentWrapper>
      {comments?.length > 0 && (
        <>
          <Text type="h2" mt={40} mb={mediaIsPhone ? 16 : 24}>
            {comments?.length === 1
              ? '1 commento'
              : `${comments.length} commenti`}
          </Text>
          {comments.map((comment, i) => (
            <CommentWrapper key={`comment-${i}`}>
              <RenderComment
                comment={comment}
                allow_reply={allow_reply}
                sendReply={handleSendReply}
              />
            </CommentWrapper>
          ))}
        </>
      )}
    </div>
  );
};

DocumentExpertComments.propTypes = {
  // HOC: mediaQueries
  mediaIsPhone: PropTypes.bool,
  // HOC: state
  comments: PropTypes.array,
  username: PropTypes.object,
  allow_reply: PropTypes.bool,
  // HOC: dispatch
  getComments: PropTypes.func.isRequired,
  sendComment: PropTypes.func.isRequired,
  sendReply: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const { comments: { allow_reply, items: comments } = {} } =
      state.documentsExpert;
    const username = state.user;
    return {
      comments,
      allow_reply,
      username,
    };
  },
  (dispatch) => ({
    getComments: (id) =>
      dispatch({ type: DOCUMENT_EXPERT_GET_COMMENTS._REQUEST, id }),
    sendComment: ({ id, body }) =>
      dispatch({ type: DOCUMENT_EXPERT_SET_COMMENT._REQUEST, id, body }),
    sendReply: ({ id, cid, body }) =>
      dispatch({
        type: DOCUMENT_EXPERT_SET_COMMENT_REPLY._REQUEST,
        id,
        cid,
        body,
      }),
    // setCommentVisibility: ({ id, published }) =>
    //   dispatch({ type: COMMENT_VISIBILITY_SET._REQUEST, id, published }),
  })
)(withMediaQueries(DocumentExpertComments));
